import React, {useEffect, useState} from 'react';
import {Helmet} from 'react-helmet';
import {Row, Col, Card, Statistic, Progress, Typography, Space, Divider, Steps, Badge} from 'antd';
import {
    PlusCircleOutlined,
    FireOutlined,
    CheckCircleOutlined,
    ThunderboltOutlined
} from '@ant-design/icons';
import {
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Area,
    AreaChart
} from 'recharts';
import * as API_STATISTIC from '../../../../shared/http/statistic'

const {Title, Text} = Typography;

const LANGUAGE_LEVELS = [
    { label: 'A1 (Начальный)', target: 500, color: '#f5222d' },
    { label: 'A2 (Элементарный)', target: 1000, color: '#fa8c16' },
    { label: 'B1 (Средний)', target: 2000, color: '#fadb14' },
    { label: 'B2 (Выше среднего)', target: 4000, color: '#52c41a' },
    { label: 'C1 (Продвинутый)', target: 8000, color: '#1890ff' },
    { label: 'C2 (В совершенстве)', target: 16000, color: '#722ed1' },
];

const StatisticPage = () => {

    const [todayData, setTodayData] = useState({addCount: 0, repeatCount: 0});
    const [loading, setLoading] = useState(true);

    const [chartData, setChartData] = useState([]);
    const [chartLoading, setChartLoading] = useState(true);

    const [masteredData, setMasteredData] = useState({ total: 0, weekly: 0 });
    const [masteredDataLoading, setMasteredDataLoading] = useState(false);


    const currentLevel = LANGUAGE_LEVELS.find(lvl => masteredData.total < lvl.target) || LANGUAGE_LEVELS[5];

    const wordsTarget = currentLevel.target;
    const progressPercent = Math.min(Math.round((masteredData.total / wordsTarget) * 100), 100);
    const wordsRemaining = Math.max(wordsTarget - masteredData.total, 0);

    const loadTodayStatistic = async () => {
        try {
            setLoading(true);
            const response = await API_STATISTIC.getTodayStatistic();
            //@ts-ignore
            if (response.success && response.data) {
                //@ts-ignore
                setTodayData(response.data);
            }
        } catch (err) {
            console.error("Ошибка загрузки статистики:", err);
        } finally {
            setLoading(false);
        }
    };

    const loadMasteredWordsStatistic = async () => {
        try {
            setMasteredDataLoading(true);
            const response = await API_STATISTIC.getMasteredWordCount();

            //@ts-ignore
            if (response.success && response.data) {
                setMasteredData({
                    //@ts-ignore
                    total: response.data.total,
                    //@ts-ignore
                    weekly: response.data.weekly
                });
            }
        } catch (err) {
            console.error("Ошибка загрузки статистики Mastered:", err);
        } finally {
            setMasteredDataLoading(false);
        }
    };


    const loadWeekChartStatistic = async () => {
        try {
            setChartLoading(true);
            const response = await API_STATISTIC.getWordsWeekChartStatistic();

            //@ts-ignore
            if (response.success && response.data?.content) {
                //@ts-ignore
                const formattedData = response.data.content.map((item: any) => ({
                    ...item,
                    day: item.day.charAt(0).toUpperCase() + item.day.slice(1)
                }));
                setChartData(formattedData);
            }
        } catch (err) {
            console.error("Ошибка загрузки статистики:", err);
        } finally {
            setChartLoading(false);
        }
    };


    useEffect(() => {
        loadTodayStatistic();
        loadWeekChartStatistic();
        loadMasteredWordsStatistic();
    }, [])


    return (
        <div style={{padding: '24px', maxWidth: '1200px', margin: '0 auto', background: '#f0f2f5', minHeight: '100vh'}}>
            <Helmet title="Voxlingua: Статистика"/>

            <Title level={2}>Статистика</Title>

            {/* Секция: Активность за сегодня */}
            <Divider orientation={"left" as any}>Сегодня</Divider>
            <Row gutter={[16, 16]} style={{marginBottom: '24px'}}>
                <Col xs={24} sm={12}>
                    <Card bordered={false} hoverable loading={loading}>
                        <Statistic
                            title="Повторено сегодня"
                            value={todayData.repeatCount} // Используем данные из API
                            valueStyle={{color: '#1890ff'}}
                            prefix={<FireOutlined/>}
                            suffix="слов"
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12}>
                    <Card bordered={false} hoverable loading={loading}>
                        <Statistic
                            title="Добавлено сегодня"
                            value={todayData.addCount} // Используем данные из API
                            valueStyle={{color: '#52c41a'}}
                            prefix={<PlusCircleOutlined/>}
                            suffix="слов"
                        />
                    </Card>
                </Col>
            </Row>

            {/* Секция: Прогресс и закрепление (Пункты 5 и 6) */}
            <Divider orientation={"left" as any}>Успехи в обучении</Divider>


            {/* Прогресс уровня детально */}
            <Card
                title={`Цель: Уровень ${currentLevel.label}`}
                bordered={false}
                style={{ marginBottom: '24px' }}
                extra={<Text type="secondary">Цель: {wordsTarget} слов</Text>}
            >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Progress
                        percent={progressPercent}
                        status={progressPercent === 100 ? "success" : "active"}
                        strokeColor={{
                            '0%': '#108ee9',
                            '100%': currentLevel.color, // Цвет меняется в зависимости от уровня
                        }}
                        strokeWidth={15}
                        format={(percent) => (
                            <span style={{ fontWeight: 'bold' }}>{percent}%</span>
                        )}
                    />

                    <div style={{ textAlign: 'center', backgroundColor: 'var(--color-bg-elevated)', padding: '12px', borderRadius: '8px' }}>
                        <Text strong style={{ fontSize: '16px' }}>
                            Вы закрепили {masteredData.total} {masteredData.total === 1 ? 'слово' : 'слов'}.
                        </Text>
                        <br />
                        {wordsRemaining > 0 ? (
                            <Text type="secondary">
                                Осталось всего <Text strong>{wordsRemaining}</Text> до достижения уровня {currentLevel.label.split(' ')[0]}!
                            </Text>
                        ) : (
                            <Text type="success" strong>🎉 Поздравляем! Вы достигли максимума для этого уровня!</Text>
                        )}
                    </div>
                </Space>
            </Card>


            {/* График активности */}
            <Card style={{marginBottom: '24px'}} title="Динамика недели" bordered={false} loading={chartLoading}>
                <div style={{width: '100%', height: 350}}>
                    <ResponsiveContainer>
                        <AreaChart data={chartData} margin={{top: 20, right: 30, left: 0, bottom: 0}}>
                            <defs>
                                {/* Градиент для линии "Повторено" */}
                                <linearGradient id="colorRepeated" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#1890ff" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#1890ff" stopOpacity={0}/>
                                </linearGradient>
                                {/* Градиент для линии "Добавлено" */}
                                <linearGradient id="colorAdded" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#52c41a" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#52c41a" stopOpacity={0}/>
                                </linearGradient>
                            </defs>

                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0"/>
                            <XAxis
                                dataKey="day"
                                axisLine={false}
                                tickLine={false}
                                padding={{left: 10, right: 10}}
                            />
                            <YAxis axisLine={false} tickLine={false}/>
                            <Tooltip
                                contentStyle={{
                                    borderRadius: '8px',
                                    border: 'none',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }}
                            />
                            <Legend verticalAlign="top" align="right" iconType="circle"
                                    wrapperStyle={{paddingBottom: '20px'}}/>

                            <Area
                                name="Повторено"
                                type="monotone"
                                dataKey="repeated"
                                stroke="#1890ff"
                                strokeWidth={3}
                                fillOpacity={1}
                                fill="url(#colorRepeated)" // Ссылка на градиент выше
                                dot={{r: 4, fill: '#1890ff', strokeWidth: 2}}
                                activeDot={{r: 6}}
                            />

                            <Area
                                name="Добавлено"
                                type="monotone"
                                dataKey="added"
                                stroke="#52c41a"
                                strokeWidth={3}
                                fillOpacity={1}
                                fill="url(#colorAdded)" // Ссылка на градиент выше
                                dot={{r: 4, fill: '#52c41a', strokeWidth: 2}}
                                activeDot={{r: 6}}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </Card>
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                {/* 5. Закреплено на этой неделе */}
                <Col xs={24} md={12} style={{ display: 'flex' }}>
                    <Card
                        bordered={false}
                        hoverable
                        loading={masteredDataLoading}
                        style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
                        bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                    >
                        <Statistic
                            title="Закреплено на этой неделе"
                            value={masteredData.weekly}
                            valueStyle={{ color: '#722ed1' }}
                            prefix={<ThunderboltOutlined />}
                            suffix="слов"
                        />
                        {/* margin-top: auto прижмет текст к низу, если контента мало */}
                        <div style={{ marginTop: 'auto', paddingTop: '12px' }}>
                            <Text type="secondary">
                                {masteredData.weekly > 10
                                    ? "Супер! Это отличный прогресс за 7 дней."
                                    : "На этой неделе пока нет закрепленных слов."}
                            </Text>
                        </div>
                    </Card>
                </Col>

                {/* 6. Всего закреплено */}
                <Col xs={24} md={12} style={{ display: 'flex' }}>
                    <Card
                        bordered={false}
                        hoverable
                        loading={masteredDataLoading}
                        style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
                        bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                    >
                        <Statistic
                            title="Всего закреплено (Mastered)"
                            value={masteredData.total}
                            valueStyle={{ color: '#eb2f96' }}
                            prefix={<CheckCircleOutlined />}
                            suffix="слов"
                        />
                        <div style={{ marginTop: 'auto', paddingTop: '12px' }}>
                            <Text type="secondary">Ваш активный золотой запас слов.</Text>
                        </div>
                    </Card>
                </Col>
            </Row>
            <Card title="Этапы запоминания" bordered={false} style={{ marginBottom: '24px' }}>
                <div style={{ overflowX: 'auto', paddingBottom: '10px' }}>
                    <Steps
                        size="small"
                        current={-1} // Чтобы все шаги выглядели доступными, но не "пройденными"
                        status="wait"
                        items={[
                            { title: '1 д', description: 'Старт' },
                            { title: '3 д', description: 'Память' },
                            { title: '7 д', description: 'Неделя' },
                            { title: '14 д', description: 'Закрепление' },
                            { title: '30 д', description: 'Месяц' },
                            { title: '60 д', description: '2 месяца' },
                            { title: '120 д', description: 'Полгода' },
                            {
                                title: <Badge status="success" text="240д" />,
                                description: <b>Mastered</b>,
                                subTitle: '🎉'
                            },
                        ]}
                    />
                </div>
                <Divider dashed style={{ margin: '12px 0' }} />
                <Text type="secondary" italic>
                    * Каждая успешная итерация увеличивает интервал. Если вы забыли слово, прогресс сбрасывается.
                    Слова считаются <b>закрепленными</b> после 7-го этапа.
                </Text>
            </Card>
        </div>
    );
};

export default StatisticPage;